<template>
  <div>
    <v-sheet
      v-if="dataLoading"
      :color="'grey lighten-4'"
      class="pa-3"
    >
      <v-skeleton-loader type="card-heading, table-row-divider">
      </v-skeleton-loader>
    </v-sheet>
    <div v-else>
      <naj-box :title="local.bankruptcy.subRequests.requestApplicantInfo">
        <div>
          <v-row>
            <v-col
              cols="12"
              md="12"
              lg="12"
            >
              <naj-input
                :type-id="formFieldType.text"
                :label="applicantNameLabel"
                :value.sync="formModel.requestApplicant.applicantName"
                :disabled="true"
              >
              </naj-input>
            </v-col>
          </v-row>
        </div>
      </naj-box>
      <naj-box
        v-if="showOnBehalfSection"
        :title="local.bankruptcy.subRequests.originalPartyInfo"
      >
        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="3"
          >
            <naj-input
              :type-id="formFieldType.selectbox"
              :value.sync="formModel.requestApplicant.requestOwnerTypeId"
              :label="local.bankruptcy.subRequests.requestApplicantForm.onBehalfOf"
              :items="allowedRequestOwnerTypes"
              item-value="id"
              item-text="name"
              :disabled="isViewMode || !isObjectionRequest"
            >
            </naj-input>
          </v-col>
          <v-col
            v-if="isStakeholder"
            cols="12"
            md="6"
            lg="3"
          >
            <naj-input
              :type-id="formFieldType.selectbox"
              :value.sync="formModel.requestApplicant.stakeholderTypeId"
              :label="local.bankruptcy.subRequests.requestApplicantForm.stakeholderType"
              :items="stakeholderTypes"
              item-value="id"
              item-text="name"
              :disabled="true"
            >
            </naj-input>
          </v-col>
        </v-row>
        <v-row v-if="showFirmTable">
          <v-col cols="12">
            <identity-verification-form
              :key="organizationKey"
              :form-model="organizationData"
              :person-title="local.bankruptcy.common.theCreditor"
              :view-mode="true"
              :is-table-data="true"
            ></identity-verification-form>
          </v-col>
        </v-row>
      </naj-box>

      <parent-request-info
        v-if="formModel.parentSubRequestObjectKey && formModel.objectKey"
        :parent-judgements="formModel.parentJudgements"
        :parent-sessions="formModel.parentSessions"
      >
      </parent-request-info>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapActions } = createNamespacedHelpers('BankruptcyModule');
import {
  legalCapacitiesEnum,
  requestOwnerTypeEnum,
  stakeholderTypesEnum,
  partyTypeEnum,
  creditorClassificationsEnum,
  subRequestType
} from '@/modules/bankruptcy/helpers/enum';
import { socialIdTypeEnum } from '@/helpers/enum';
import IdentityVerificationForm from '@/modules/bankruptcy/components/request/identity-verification-form.vue';
import ParentRequestInfo from './parent-request-info.vue';

export default {
  name: 'SubRequestApplicantOnlyName',
  inject: ['$validator'],
  components: {
    IdentityVerificationForm,
    ParentRequestInfo,
  },
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    isNewMode: {
      type: Boolean,
      required: true,
    },
    isViewMode: {
      type: Boolean,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      dataLoading: null,
      legalCapacitiesEnum,
      requestApplicant: {},
      firmHeader: [
        {
          value: 'organizationName',
          text: this.local.bankruptcy.identityVerificationForm.theName,
        },
        {
          value: 'unifiedNumber',
          text: this.local.bankruptcy.requestDetails.bankruptDebts.unifiedNumber,
        },
        {
          value: 'commercialNumber',
          text: this.local.bankruptcy.requestDetails.bankruptDebts.commercialRegistrationNumber,
        },
      ],
      partyInfo: {},
      requestOwnerTypeEnum,
      requestParties: {},
      stakeholderTypesEnum,
      partyTypeEnum,
      subRequestType,
      creditorClassificationsEnum,
    };
  },
  computed: {
    ...mapState(['legalCapacities', 'currentUser', 'allRequestOwnerTypes', 'stakeholderTypes']),
    userInfo() {
      if (!this.currentUser) return {};
      return this.currentUser;
    },
    organizationKey() {
      return `${this.formModel.requestApplicant.requestOwnerTypeId}-${this.isDebtorRequestOwner}`;
    },
    allowedRequestOwnersForGov(){
      if(this.formModel.requestApplicant.legalCapacityId == legalCapacitiesEnum.bankruptcySecretary){
        return this.allRequestOwnerTypes.filter(lc =>
              lc.id == requestOwnerTypeEnum.debtor ||
              lc.id == requestOwnerTypeEnum.bankruptcyCommission
            ).map((x) => {
              if (x.id == requestOwnerTypeEnum.bankruptcyCommission) {
                x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
              }
              return x;
          }
        );
      }
      return this.allRequestOwnerTypes.filter(lc =>
          lc.id == requestOwnerTypeEnum.stakeholder
      );
    },
    allowedRequestOwnersForBusiness(){
      const isDebtorRepresentative = 
        this.userInfo.nationalRecordNumber === this.formModel.requestApplicant.debtor.unifiedNumber || 
        this.userInfo.commercialRecordNumber === this.formModel.requestApplicant.debtor.commercialRegistrationNumber;

      if(this.isTrusteeApplying){
        return this.allRequestOwnerTypes.filter(lc =>
            (lc.id == requestOwnerTypeEnum.debtor && isDebtorRepresentative) ||
            lc.id == requestOwnerTypeEnum.trustee
          ).map((x) => {
            if (x.id == requestOwnerTypeEnum.trustee) {
              x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
            }
            return x;
          });
      }
      return this.allRequestOwnerTypes.filter(lc =>
          (lc.id == requestOwnerTypeEnum.debtor && isDebtorRepresentative) ||
          (lc.id == requestOwnerTypeEnum.stakeholder && !isDebtorRepresentative)
      );
    },
    allowedRequestOwnerTypes() {
      if(this.isViewMode){
        return this.allRequestOwnerTypes.map((x) => {
              if (x.id == requestOwnerTypeEnum.bankruptcyCommission || x.id == legalCapacitiesEnum.trustee) {
                x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
              }
              return x;
          });
      }
      if(this.isBusiness){
        return this.allowedRequestOwnersForBusiness;
      }
      else {
        return this.allowedRequestOwnersForGov;
      }
    },
    isOrganizationUser() {
      return this.userInfo.isOrganizationUser;
    },
    organizationData() {
      if(this.isDebtorRequestOwner){
        return this.formModel.requestApplicant.debtor;
      }
      if(this.isTrusteeRequestOwner && this.isBusiness){
        return {
          ...this.formModel.requestApplicant,
          fullName: this.formModel.requestApplicant.applicantName,
          identityTypeName: socialIdTypeEnum[this.formModel.requestApplicant.identityTypeId],
          unifiedNumber: this.formModel.requestApplicant.identityNumber,
          typeId: 1,
        };
      }
      if (this.isNewMode) {
        return {
          organizationName: this.userInfo.fullName,
          unifiedNumber: this.userInfo.nationalRecordNumber,
          commercialRegistrationNumber: this.userInfo.commercialRecordNumber,
          typeId: 2,
        };
      }
      else {
        return {
          organizationName: this.formModel.requestApplicant.organizationName,
          unifiedNumber: this.formModel.requestApplicant.unifiedNumber,
          commercialRegistrationNumber: this.formModel.requestApplicant.commercialRegistrationNumber,
          typeId: 2,
        };
      }
    },
    isOrganizationBankruptcyTrustee() {
      return this.userInfo.isBankruptcyTrustee;
    },
    isStakeholder() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.stakeholder;
    },
    showFirmTable() {
      return this.formModel.requestApplicant?.requestOwnerTypeId
            && !this.isStakeholder
            || this.isCreditor 
            || this.isPartner;
    },
    isBusiness() {
      return this.currentUser.loginType == "business"
    },
    isDebtorRequestOwner() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.debtor;
    },
    isTrusteeRequestOwner() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.trustee;
    },
    isObjectionRequest() {
      return this.subRequestType[this.$route.params.requestType] == subRequestType.VerdictObjectionRequest;
    },
    isCreditor() {
      return this.formModel.requestApplicant?.stakeholderTypeId == stakeholderTypesEnum.creditor;
    },
    isPartner() {
      return this.formModel.requestApplicant?.stakeholderTypeId == stakeholderTypesEnum.partner;
    },
    showOnBehalfSection() {
      if (this.isNewMode && this.isOrganizationBankruptcyTrustee && !this.isObjectionRequest)
        return false;

      return true;
    },
    applicantNameLabel() {
      if (!this.showOnBehalfSection)
        return this.local.bankruptcy.subRequests.requestApplicantName + '(' + this.local.bankruptcy.bankruptcyFile.legalCapacities.bankruptcyCommissionRepresentative + ')';
      return this.local.bankruptcy.subRequests.requestApplicantName;
    },
  },
  mounted() {
    this.getLegalCapacities();
    this.getAllRequestOwnerTypes();
    this.getStakeholderTypes();
    this.loadData();
  },
  created() { },
  methods: {
    ...mapActions([
      'getMainRequestOrganizationPartyInfo',
      'getLegalCapacities',
      'getAllRequestOwnerTypes',
      'getMainRequestParties',
      'inquiryMainRequestPartner',
      'inquiryMainRequestCreditor',
      'getStakeholderTypes',
      'getParentSessionsBySubRequestObjectKey',
      'getParentJudgementsBySubRequestObjectKey'
    ]),
    loadData() {
      this.dataLoading = true;

      const promises = [
        this.getMainRequestParties(this.formModel.mainRequest.objectKey),
        this.getMainRequestOrganizationPartyInfo({
          objectKey: this.formModel.mainRequest.objectKey,
          unifiedNumber: this.userInfo.nationalRecordNumber,
          crNumber: this.userInfo.commercialRecordNumber,
        }),
      ];

      if (this.formModel?.parentSubRequestObjectKey && this.formModel.objectKey) {
        promises.push(
          this.getParentSessionsBySubRequestObjectKey(this.formModel.objectKey),
          this.getParentJudgementsBySubRequestObjectKey(this.formModel.objectKey)
        );
      }

      Promise.all(promises)
        .then((res) => {
          this.requestParties = res[0].data;
          this.updateFormModel(res[0]);

          this.partyInfo = res[1].data;
          if (this.isNewMode){
            this.fillRequestApplicantData();
            this.setTypeForEditMode(res);
          }

          if (this.formModel?.parentSubRequestObjectKey && this.formModel.objectKey) {
            this.$set(this.formModel, 'parentSessions', res[2]?.data)
            this.$set(this.formModel, 'parentJudgements', res[3]?.data)
          }

        })
        .finally(() => (this.dataLoading = false));
    },
    setTypeForEditMode(res) {
      if (this.formModel.mainRequest.isRequestOwner) {
        this.$set(this.formModel, 'applicantDetailsId', res[0].data.requestApplicant.id);
      }
      else if (!this.formModel.mainRequest.isRequestOwner && this.formModel.isOriginal) {
        this.$set(this.formModel.requestApplicant, 'legalCapacityId', this.formModel.originalLegalCapacityId);
        if ([legalCapacitiesEnum.bankrupt, legalCapacitiesEnum.debtor, legalCapacitiesEnum.competentParty].includes(this.formModel.originalLegalCapacityId)) {

          this.setOriginalPartyForDebtor();
        }
        else if (this.formModel.requestApplicant.requestOwnerTypeId === this.requestOwnerTypeEnum.creditor) {
          this.setOriginalPartyForCreditor();
        }
        else {
          this.fillStakeholderData(this.formModel.originalLegalCapacityId);
        }
      }
    },
    setOriginalPartyForDebtor() {
      this.$set(this.formModel, 'originalParty', this.formModel.requestApplicant.debtor);
      this.$set(this.formModel.requestApplicant, 'bankruptId', this.formModel.requestApplicant.debtor?.bankruptId);
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.requestOwnerTypeEnum.debtor);
      this.$set(this.formModel.requestApplicant, 'legalCapacityName', this.legalCapacities.find(lc => lc.id === this.formModel.requestApplicant.legalCapacityId).name);
    },
    setOriginalPartyForCreditor() {
      this.$set(this.formModel, 'originalParty', this.formModel.requestApplicant.creditor);
      this.$set(this.formModel.requestApplicant, 'creditorId', this.formModel.requestApplicant.creditor?.creditorId);
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.requestOwnerTypeEnum.creditor);
      this.$set(this.formModel.requestApplicant, 'legalCapacityName', this.legalCapacities.find(lc => lc.id === this.formModel.requestApplicant.legalCapacityId).name);
    },
    fillStakeholderData(originalLegalCapacityId) {
      this.$set(this.formModel, 'requestApplicant', {
        ...this.formModel.requestApplicant,
        requestOwnerTypeId: this.requestOwnerTypeEnum.stakeholder,
        requestOwnerObjectKey: this.isPartner ? this.formModel.requestApplicant.partner?.objectKey : this.isDebtorEmployee ? this.formModel.requestApplicant.debtorEmployee?.objectKey : this.isCreditor ? this.formModel.requestApplicant.creditor?.objectKey : '',
      });

      let stakeholderType = this.isPartner ? 'partner' : this.isDebtorEmployee ? 'debtorEmployee' : this.isCreditor ? 'creditor' : '';

      if (originalLegalCapacityId == legalCapacitiesEnum.creditor) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.creditor);
        stakeholderType = 'creditor';
      }
      else if (originalLegalCapacityId == legalCapacitiesEnum.partner) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.partner);
        stakeholderType = 'partner';
      }
      else if (originalLegalCapacityId == legalCapacitiesEnum.employee) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.employee);
        stakeholderType = 'debtorEmployee';
      }
      if (stakeholderType != '')
        this.$set(this.formModel.requestApplicant, stakeholderType, this.getCurrentUserModel());
    },
    getCurrentUserModel() {
      const names = [
        this.userInfo.firstName,
        this.userInfo.fatherName,
        this.userInfo.grandFatherName,
        this.userInfo.familyName,
      ];
      const fullName = names.filter(n => n).join(' ');
      const socialTypeId = this.userInfo.socialIdType;
      let user = {
        fullName: fullName,
        applicantName: this.isViewMode ? this.requestApplicant.applicantName : fullName,
        isIdentityVerified: true,
        identityTypeId: socialTypeId,
        identityNumber: this.userInfo.socialId,
        firstName: this.userInfo.firstName,
        fatherName: this.userInfo.fatherName,
        thirdName: this.userInfo.grandFatherName,
        familyName: this.userInfo.familyName,
        dateOfBirth: this.userInfo.dateOfBirth,
        nationality: socialTypeId === socialIdentityTypeEnum.nationalId ? countriesEnum.saudiArabia : this.userInfo.nationalityCountryId,
        gender: this.userInfo.gender,
        userType: this.userInfo.userType,
      };
      return user;
    },
    setupNewRepresentative(currentUser, data) {
      this.requestApplicant = this.formModel.requestApplicant;
      this.$set(this.formModel, 'requestApplicant', {
        ...currentUser,
        applicantName: this.requestApplicant.applicantName || currentUser.applicantName,
        debtor: data.data.debtor,
        creditor: data.data.creditor,
      });
      this.$set(this.formModel, 'representative', currentUser);
      this.dataLoading = false;
    },
    updateFormModel({ data }) {
      this.$set(this.formModel, 'isOriginal', true);
      this.$set(this.formModel, 'originalLegalCapacityId', data.originalLegalCapacityId);
      this.$set(this.formModel, 'SubRequestTypeId', subRequestType[this.$route.params.requestType]);
    },
    fillRequestApplicantData() {
      const requestApplicantUserModel = {
        applicantName: [
          this.userInfo.impersonatedUser.firstName,
          this.userInfo.impersonatedUser.middleName,
          this.userInfo.impersonatedUser.thirdName,
          this.userInfo.impersonatedUser.familyName,
        ]
          .filter(n => n)
          .join(' '),
        socialId: this.userInfo.impersonatedUser.socialId,
        socialIdType: this.userInfo.impersonatedUser.socialTypeId,
        nationality: this.userInfo.impersonatedUser.nationalityId,
        gender: this.userInfo.impersonatedUser.gender,
        dateOfBirth: this.userInfo.impersonatedUser.dateOfBirth,
        firstName: this.userInfo.impersonatedUser.firstName,
        fatherName: this.userInfo.impersonatedUser.middleName,
        grandFatherName: this.userInfo.impersonatedUser.thirdName,
        familyName: this.userInfo.impersonatedUser.familyName,
        commercialRegistrationNumber: this.userInfo.commercialRecordNumber,
        unifiedNumber: this.userInfo.nationalRecordNumber,

      };
      this.$set(this.formModel, 'requestApplicant', {
        ...requestApplicantUserModel,
        ...this.formModel.requestApplicant,
      });

      this.$set(
        this.formModel.requestApplicant,
        'legalCapacityId',
        this.isOrganizationBankruptcyTrustee
          ? legalCapacitiesEnum.bankruptcySecretary
          : legalCapacitiesEnum.administrativeEntityRepresentative
      );
      this.$set(this.formModel, 'isOriginal', this.partyInfo.isOriginal);
      this.$set(this.formModel.requestApplicant, 'creditor', null);
      this.$set(this.formModel.requestApplicant, 'debtorEmployee', null);
      this.$set(this.formModel.requestApplicant, 'partner', null);
      this.$set(this.formModel.requestApplicant, 'debtor', null);

      this.$set(this.formModel.requestApplicant, 'applicantName', requestApplicantUserModel.applicantName);
      this.$set(this.formModel.requestApplicant, 'fullName', requestApplicantUserModel.applicantName);
      this.$set(this.formModel.requestApplicant, 'identityTypeId', requestApplicantUserModel.socialIdType);
      this.$set(this.formModel.requestApplicant, 'identityNumber', requestApplicantUserModel.socialId);
      this.$set(this.formModel.requestApplicant, 'firstName', requestApplicantUserModel.firstName);
      this.$set(this.formModel.requestApplicant, 'fatherName', requestApplicantUserModel.fatherName);
      this.$set(this.formModel.requestApplicant, 'thirdName', requestApplicantUserModel.grandFatherName);
      this.$set(this.formModel.requestApplicant, 'familyName', requestApplicantUserModel.familyName);
      this.$set(this.formModel.requestApplicant, 'dateOfBirth', requestApplicantUserModel.dateOfBirth);
      this.$set(this.formModel.requestApplicant, 'nationality', requestApplicantUserModel.nationality);
      this.$set(this.formModel.requestApplicant, 'gender', requestApplicantUserModel.gender);
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.getRequestOwnerType());

      this.$set(this.formModel.requestApplicant, 'debtor', this.requestParties.debtor);
      this.$set(this.formModel.requestApplicant, 'bankruptId', this.requestParties.debtor.bankruptId);
      

      if (this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.stakeholder) {
        this.loading = true;
        const payload = {
          objectKey: this.formModel.mainRequest.objectKey,
          commercialRegistrationNumber: this.userInfo.commercialRecordNumber,
          unifiedNumber: this.userInfo.nationalRecordNumber,
        };
        if (this.partyInfo.legalCapacityId == legalCapacitiesEnum.creditor) {
          this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.creditor);
          this.inquiryMainRequestCreditor(payload)
            .then((res) => {
              if (res.data) {
                res.data.isRetrieved = true;
                this.$set(this.formModel.requestApplicant, 'creditor', res.data);
              }
            })
            .finally(() => (this.loading = false));
        }

        if (this.partyInfo.legalCapacityId == legalCapacitiesEnum.partner) {
          this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.partner);
          this.inquiryMainRequestPartner(payload)
            .then((res) => {
              if (res.data) {
                res.data.isRetrieved = true;
                this.$set(this.formModel.requestApplicant, 'partner', res.data);
              }
            })
            .finally(() => (this.loading = false));
        }
      }
      this.requestApplicant = this.formModel.requestApplicant;
    },
    getRequestOwnerType() {
      if (this.requestParties.requestOwnerTypeId) return this.requestParties.requestOwnerTypeId;

      if (this.userInfo.isBankruptcyTrustee)
        return requestOwnerTypeEnum.bankruptcyCommission;

      if (
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.debtor ||
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.competentParty ||
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.bankrupt
      ) return requestOwnerTypeEnum.debtor;

      if (
        !this.partyInfo ||
        !this.partyInfo.legalCapacityId ||
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.partner ||
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.creditor
      )
        return requestOwnerTypeEnum.stakeholder;

      return this.formModel.mainRequest.requestOwnerTypeId;
    },
  },
};
</script>
